/**
 * 周报：GET /api/app/core/video/weekly-statistics
 */
import axios from 'axios'

/** 本地时间格式化为接口所需：2026-03-30 00:00:00 */
export function formatLocalDateTimeParam(d) {
  const x = new Date(d)
  const pad = (n) => String(n).padStart(2, '0')
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())} ${pad(x.getHours())}:${pad(x.getMinutes())}:${pad(x.getSeconds())}`
}

/**
 * 自然周 [周一 00:00:00, 下周一 00:00:00)
 * @param {Date} mondayDate getMondayOfWeek 得到的周一日期
 */
export function getWeekStatisticsRange(mondayDate) {
  const start = new Date(mondayDate.getFullYear(), mondayDate.getMonth(), mondayDate.getDate(), 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 7)
  return {
    startDate: formatLocalDateTimeParam(start),
    endDate: formatLocalDateTimeParam(end)
  }
}

/**
 * 将 weekly-statistics 接口体转为周报页 overview + 标签列表
 * @param {string} weekId 展示用 ISO 周次
 */
export function mapWeeklyStatisticsToPageData(raw, weekId) {
  const o = raw && typeof raw === 'object' ? raw : {}
  const thisWeekVideoCount = Number(o.thisWeekVideoCount ?? 0) || 0
  const thisWeekSavedHours = Number(o.thisWeekSavedHours ?? 0) || 0
  const lastWeekVideoCount = Number(o.lastWeekVideoCount ?? 0) || 0
  const lastWeekSavedHoursRaw = o.lastWeekSavedHours
  const lastWeekSavedHours =
    lastWeekSavedHoursRaw != null && lastWeekSavedHoursRaw !== ''
      ? Number(lastWeekSavedHoursRaw)
      : NaN
  const lastWeekTimeMinutes = Number.isFinite(lastWeekSavedHours) ? lastWeekSavedHours * 60 : undefined

  const top5 = Array.isArray(o.top5Labels) ? o.top5Labels : []
  const hotTags = top5
    .map((row) => {
      const name = String(row.label ?? row.name ?? '').trim()
      const count = Number(row.count ?? 0) || 0
      return name ? { name, count } : null
    })
    .filter(Boolean)

  const statsObj = o.thisWeekLabelStats && typeof o.thisWeekLabelStats === 'object' ? o.thisWeekLabelStats : {}
  const userLabelTags = Object.keys(statsObj)
    .map((name) => ({ name: String(name).trim(), count: Number(statsObj[name]) || 0 }))
    .filter((x) => x.name)
    .sort((a, b) => b.count - a.count)

  const overview = {
    weekId: String(weekId || ''),
    videoCount: thisWeekVideoCount,
    timeSavedMinutes: thisWeekSavedHours * 60,
    statusCounts: {
      completed: thisWeekVideoCount,
      inProgress: 0,
      failed: 0
    },
    lastWeek: {
      videoCount: lastWeekVideoCount,
      timeSavedMinutes: lastWeekTimeMinutes
    }
  }

  return { overview, userLabelTags, hotTags }
}

/**
 * GET /api/app/core/video/weekly-statistics?userId=&startDate=&endDate=
 * @param {string|null} userId
 * @param {string} startDate 如 2026-03-30 00:00:00
 * @param {string} endDate 如 2026-04-06 00:00:00
 * @param {string} weekId 与周选择器对应的 ISO 周次（仅写入 overview.weekId）
 */
export async function fetchWeeklyStatistics(_userId, startDate, endDate, weekId) {
  const { data: body } = await axios.get('/api/app/core/video/weekly-statistics', {
    params: { startDate, endDate }
  })
  return mapWeeklyStatisticsToPageData(body, weekId)
}

/** 兼容后端 camelCase / 旧版 snake_case，统一为页面使用的 snake_case */
function normalizeMbtiUserSummary(raw) {
  if (!raw || typeof raw !== 'object') return null
  const title = raw.title != null ? String(raw.title) : ''
  const pt = raw.personality_type != null ? raw.personality_type : raw.personalityType
  const pp = raw.population_percentage != null ? raw.population_percentage : raw.populationPercentage
  const at = raw.analysis_text != null ? raw.analysis_text : raw.analysisText
  return {
    title,
    personality_type: pt != null ? String(pt).trim() : '',
    population_percentage: pp,
    analysis_text: at != null ? String(at) : ''
  }
}

function normalizeMbtiDimensions(arr) {
  if (!Array.isArray(arr)) return []
  return arr
    .map((row) => {
      if (!row || typeof row !== 'object') return null
      const lc = row.left_code != null ? row.left_code : row.leftCode
      const ll = row.left_label != null ? row.left_label : row.leftLabel
      const rc = row.right_code != null ? row.right_code : row.rightCode
      const rl = row.right_label != null ? row.right_label : row.rightLabel
      const lean = row.leaning != null ? String(row.leaning).trim().toLowerCase() : ''
      return {
        left_code: lc != null ? String(lc) : '',
        left_label: ll != null ? String(ll) : '',
        right_code: rc != null ? String(rc) : '',
        right_label: rl != null ? String(rl) : '',
        score: row.score,
        leaning: lean === 'right' || lean === 'left' ? lean : ''
      }
    })
    .filter(Boolean)
}

function unwrapMbtiResultResponse(body) {
  const o = body && typeof body === 'object' ? body : {}
  if (Object.prototype.hasOwnProperty.call(o, 'code')) {
    const n = typeof o.code === 'string' ? Number(String(o.code).trim()) : Number(o.code)
    if (Number.isNaN(n) || n !== 200) {
      const msg = o.msg != null && String(o.msg).trim() !== '' ? String(o.msg) : 'MBTI 数据请求失败'
      throw new Error(msg)
    }
  }
  const d = o.data != null && typeof o.data === 'object' ? o.data : o
  const usRaw = d.user_summary != null ? d.user_summary : d.userSummary
  const dimsRaw = d.personality_dimensions != null ? d.personality_dimensions : d.personalityDimensions
  return {
    user_summary: normalizeMbtiUserSummary(usRaw && typeof usRaw === 'object' ? usRaw : null),
    personality_dimensions: normalizeMbtiDimensions(dimsRaw)
  }
}

/**
 * POST /api/app/core/mbti/result
 * @param {string} userId 当前用户标识（与登录落库的 userId 一致）
 */
export async function fetchMbtiResult() {
  const { data: body } = await axios.post('/api/app/core/mbti/result', {})
  return unwrapMbtiResultResponse(body)
}
